import {
  type ButtonHTMLAttributes,
  cloneElement,
  type InputHTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from "react";

import { CloseIcon } from "./icons";

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <span className="spinner" role="status">
      <span className="spinner__ring" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  busy?: boolean;
  tone?: "primary" | "secondary" | "quiet" | "danger";
};

export function Button({
  busy = false,
  children,
  className = "",
  disabled,
  tone = "primary",
  ...props
}: ButtonProps) {
  return (
    <button
      className={`button button--${tone} ${className}`.trim()}
      disabled={disabled || busy}
      {...props}
    >
      {busy ? <Spinner label="Working" /> : null}
      <span>{children}</span>
    </button>
  );
}

export function Notice({
  children,
  title,
  tone = "neutral",
}: PropsWithChildren<{
  title?: string;
  tone?: "neutral" | "success" | "warning" | "danger";
}>) {
  return (
    <div className={`notice notice--${tone}`} role={tone === "danger" ? "alert" : "status"}>
      {title ? <strong>{title}</strong> : null}
      <div>{children}</div>
    </div>
  );
}

export function Field({
  children,
  hint,
  label,
}: {
  children: ReactElement<InputHTMLAttributes<HTMLInputElement>>;
  label: string;
  hint?: string;
}) {
  const generatedId = useId();
  const controlId = children.props.id ?? `field-${generatedId}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const control = cloneElement(children, {
    id: controlId,
    "aria-describedby": children.props["aria-describedby"] ?? hintId,
  });

  return (
    <div className="field">
      <label className="field__label" htmlFor={controlId}>{label}</label>
      {control}
      {hint ? <span className="field__hint" id={hintId}>{hint}</span> : null}
    </div>
  );
}

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function ConfirmationDialog({
  actions,
  children,
  onClose,
  open,
  title,
}: {
  actions: ReactNode;
  children: ReactNode;
  onClose: () => void;
  open: boolean;
  title: string;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const firstFocusable =
      panel?.querySelector<HTMLElement>("[data-dialog-initial-focus]") ??
      panel?.querySelector<HTMLElement>(focusableSelector);
    firstFocusable?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panel) {
        return;
      }

      const focusable = [...panel.querySelectorAll<HTMLElement>(focusableSelector)];
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className="dialog"
        ref={panelRef}
        role="dialog"
      >
        <button className="dialog__close" type="button" onClick={onClose} aria-label="Close dialog">
          <CloseIcon />
        </button>
        <h2 id={titleId}>{title}</h2>
        <div className="dialog__body">{children}</div>
        <div className="dialog__actions">{actions}</div>
      </div>
    </div>
  );
}
